import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, ImageSquare, CircleNotch } from "@phosphor-icons/react";
import { LensResultCard } from "./LensResultCard";

export const LensDialog = ({ open, onClose, threshold, onThreshold, onOpenDesign }) => {
  const cam = useRef(null);
  const gal = useRef(null);
  const [preview, setPreview] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [thr, setThr] = useState(threshold);

  const search = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setBusy(true); setRes(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/designs/lens", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setRes(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const saveThr = async () => {
    try {
      await api.put("/designs/settings", { lens_threshold: Number(thr) });
      onThreshold(Number(thr));
      toast.success(`Threshold ${thr}% save ho gaya`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto rounded-sm bg-slate-950 border-slate-800" data-testid="lens-dialog">
        <DialogHeader><DialogTitle className="pr-8">LENS — photo se design dhoondho</DialogTitle></DialogHeader>
        <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" data-testid="lens-camera-input"
          onChange={(e) => { search(e.target.files?.[0]); e.target.value = ""; }} />
        <input ref={gal} type="file" accept="image/*" className="hidden" data-testid="lens-upload-input"
          onChange={(e) => { search(e.target.files?.[0]); e.target.value = ""; }} />
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => cam.current?.click()} disabled={busy} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="lens-camera-btn">
            <Camera size={16} className="mr-1" /> CAMERA
          </Button>
          <Button onClick={() => gal.current?.click()} disabled={busy} variant="outline" className="rounded-sm border-slate-700 bg-slate-950" data-testid="lens-upload-btn">
            <ImageSquare size={16} className="mr-1" /> PHOTO UPLOAD
          </Button>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">
            Match threshold
            <Input type="number" min={50} max={99} value={thr} onChange={(e) => setThr(e.target.value)}
              className="w-20 h-8 rounded-sm bg-slate-950 border-slate-700" data-testid="lens-threshold-input" />%
            <Button size="sm" variant="outline" onClick={saveThr} className="h-8 rounded-sm border-slate-700 bg-slate-950" data-testid="lens-threshold-save">SAVE</Button>
          </div>
        </div>
        <div className="grid md:grid-cols-5 gap-4 mt-2">
          <div className="md:col-span-1">
            {preview ? <img src={preview} alt="query" className="w-full rounded-sm border border-slate-800" data-testid="lens-query-preview" />
              : <div className="text-xs text-slate-500 border border-dashed border-slate-700 rounded-sm p-4">Plate / gate ki saaf photo lo — seedhi, poori design frame me.</div>}
          </div>
          <div className="md:col-span-4">
            {busy && <div className="flex items-center gap-2 text-slate-300 text-sm" data-testid="lens-loading"><CircleNotch className="animate-spin" /> Milte-julte designs dhoondh rahe hain…</div>}
            {res && res.results.length === 0 && <div className="text-sm text-slate-400" data-testid="lens-empty">Abhi koi design Lens index me nahi hai.</div>}
            {res && res.results.length > 0 && (
              <>
                <div className="text-xs text-slate-400 mb-2" data-testid="lens-summary">{res.indexed} designs me se top 4 · threshold {res.threshold}%</div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {res.results.map((r) => <LensResultCard key={r.id} r={r} onOpen={onOpenDesign} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
